import React, { useEffect, useState } from 'react'
import { Modal } from '../Modal/Modal'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { SessionUserFilter } from './SessionUserFilter'
import { SessionDateFilter } from './SessionDateFilter'
import { SessionMeetingTemplateFilter } from './SessionMeetingTemplateFilter'
import { useLazyGetRoomSessionsForFilterQuery } from '../../apps/professional-app/fa/store/fa'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  resetSessionFilter,
  selectPastMeetingsCompositeFilter,
  selectSessionFilter,
  selectSessionFilterDateRange,
  selectSessionFilterMeetingKind,
  selectSessionFilterMeetingSources,
  selectSessionFilterMeetingTemplateIds,
  selectSessionFilterPage,
  selectSessionFilterPageSize,
  selectSessionFilterTags,
  selectSessionFilterUserIds,
  selectSessionFilterContactIds,
  selectUpcomingMeetingsCompositeFilter,
  setSessionFilterDateRange,
  setSessionFilterMeetingKind,
  setSessionFilterMeetingSources,
  setSessionFilterMeetingTemplateIds,
  setSessionFilterTags,
  setSessionQuickFilter,
} from '../../store/sessionFilter/sessionFilter'
import classNames from 'classnames'
import './SessionFilters.scss'
import { Divider } from '../Divider/Divider'
import { Button } from '../Button/Button'
import { SessionTagsFilter } from './SessionTagsFilter'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { SessionKindSourceFilter } from './SessionKindSourceFilter'
import { addToast } from '../../store/toast/toastSlice'
import { useCreateSessionFilterMutation, useDeleteSessionFilterMutation, useUpdateSessionFilterMutation } from '../../store/sessionFilter/quickFilter'
import { FormFeedback } from '../Form/FormFeedback'
import { Controller, useForm } from 'react-hook-form'
import { AllFilterModalMode, SessionQuickFilter, STICKY_SESSION_FILTER_SEARCH_PARAMS } from './model'
import { SessionContactFilter } from './SessionContactFilter'
import { useStickySearchParams } from '../../common/useStickySearchParams'

export interface AllFilterModalProps {
  show: boolean
  onHide: () => void
  mode?: AllFilterModalMode
  filterType: 'past' | 'upcoming'
  onFilterChanged?: (searchParams: URLSearchParams) => void
}

export const AllFilterModal: React.FC<AllFilterModalProps> = ({ show, onHide, mode = AllFilterModalMode.VIEW, filterType, onFilterChanged }) => {
  return (
    <Modal
      header={<></>}
      body={<AllFilterModalBody mode={mode} onHide={onHide} filterType={filterType} onFilterChanged={onFilterChanged} />}
      show={show}
      onHide={onHide}
      footer={<></>}
    />
  )
}

interface AllFilterModalBodyProps {
  onHide: () => void
  mode?: AllFilterModalMode
  filterType: 'past' | 'upcoming'
  onFilterChanged?: (searchParams: URLSearchParams) => void
}

interface CreateOrUpdateFilterFormValues {
  selectedFormFilter: SessionQuickFilter | null
  name: string
  isDefault: boolean
}

const AllFilterModalBody: React.FC<AllFilterModalBodyProps> = ({ onHide, mode = AllFilterModalMode.VIEW, filterType, onFilterChanged }) => {
  const dispatch = useAppDispatch()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = useAppSelector(selectAccountId)

  const [triggerGetRoomSessions, { data: roomSessions, isFetching: roomSessionsLoading }] = useLazyGetRoomSessionsForFilterQuery()
  const [triggerDeleteSessionFilter, { isLoading: deleteSessionFilterLoading }] = useDeleteSessionFilterMutation()
  const [triggerCreateSessionFilter, { isLoading: createSessionFilterLoading }] = useCreateSessionFilterMutation()
  const [triggerUpdateSessionFilter, { isLoading: updateSessionFilterLoading }] = useUpdateSessionFilterMutation()
  const [modalMode, setModalMode] = useState<AllFilterModalMode>(mode)

  const selectedUserIds = useAppSelector(selectSessionFilterUserIds)
  const selectedContactIds = useAppSelector(selectSessionFilterContactIds)
  const selectedDateRange = useAppSelector(selectSessionFilterDateRange)
  const selectedMeetingTypeIds = useAppSelector(selectSessionFilterMeetingTemplateIds)
  const selectedFilterTags = useAppSelector(selectSessionFilterTags)
  const selectedMeetingKind = useAppSelector(selectSessionFilterMeetingKind)
  const selectedMeetingSources = useAppSelector(selectSessionFilterMeetingSources)
  const selectedFilter = useAppSelector(selectSessionFilter)
  const selectedPage = useAppSelector(selectSessionFilterPage)
  const selectedPageSize = useAppSelector(selectSessionFilterPageSize)
  const compositeFilter = useAppSelector(filterType === 'upcoming' ? selectUpcomingMeetingsCompositeFilter : selectPastMeetingsCompositeFilter)

  const defaultValues = {
    id: null,
    name: '',
    isDefault: false,
  }

  const { control, setValue, watch, handleSubmit, reset } = useForm<CreateOrUpdateFilterFormValues>({ defaultValues })
  const selectedFormFilter = watch('selectedFormFilter')
  const disabledForEdit = !!(modalMode === AllFilterModalMode.EDIT && selectedFilter && selectedFilter.type === 'system')

  const { searchParams, setSearchParams } = useStickySearchParams(STICKY_SESSION_FILTER_SEARCH_PARAMS)

  useEffect(() => {
    if (selectedFilter) {
      setValue('selectedFormFilter', selectedFilter)
      setValue('isDefault', selectedFilter.isDefault)
      setValue('name', selectedFilter.name)
    } else {
      if (modalMode === AllFilterModalMode.VIEW) {
        reset()
      }
    }
  }, [modalMode, reset, selectedFilter, setValue])

  useEffect(() => {
    const searchParams = {
      ...(selectedFilter && { filterId: selectedFilter.id }),
      ...(selectedPage && { page: selectedPage.toString() }),
      ...(selectedPageSize && { pageSize: selectedPageSize.toString() }),
      ...(selectedUserIds && !selectedFilter && { userId: selectedUserIds.join(',') }),
      ...(selectedContactIds && !selectedFilter && { contactId: selectedContactIds.join(',') }),
      ...(selectedMeetingKind && !selectedFilter && { meetingKind: selectedMeetingKind }),
      ...(selectedMeetingSources.length && !selectedFilter && { meetingSources: JSON.stringify(selectedMeetingSources) }),
      ...(selectedDateRange && !selectedFilter && { dateRange: JSON.stringify(selectedDateRange) }),
      ...(selectedMeetingTypeIds.length && !selectedFilter && { meetingTemplates: JSON.stringify(selectedMeetingTypeIds) }),
      ...(selectedFilterTags.length && !selectedFilter && { tags: JSON.stringify(selectedFilterTags) }),
    }
    setSearchParams(searchParams, { replace: true })
  }, [
    selectedContactIds,
    selectedDateRange,
    selectedFilter,
    selectedFilterTags,
    selectedMeetingKind,
    selectedMeetingSources,
    selectedMeetingTypeIds,
    selectedPage,
    selectedPageSize,
    selectedUserIds,
    setSearchParams,
  ])

  useEffect(() => {
    triggerGetRoomSessions({
      accountId,
      ...(!selectedFilter && { filter: compositeFilter }),
      ...(selectedFilter && { filterId: selectedFilter.id }),
      pageRequest: { page: 0, size: 1 },
    })
  }, [accountId, triggerGetRoomSessions, compositeFilter, selectedFilter, searchParams, setSearchParams])

  const resetDateRangeFilter = () => {
    dispatch(setSessionFilterDateRange(null))
    searchParams.delete('dateRange')
    if (onFilterChanged) {
      onFilterChanged(searchParams)
    }
  }

  const resetMeetingTypeIds = () => {
    dispatch(setSessionFilterMeetingTemplateIds([]))
    searchParams.delete('meetingTemplates')
    if (onFilterChanged) {
      onFilterChanged(searchParams)
    }
  }

  const resetTags = () => {
    dispatch(setSessionFilterTags([]))
    searchParams.delete('tags')
    if (onFilterChanged) {
      onFilterChanged(searchParams)
    }
  }

  const resetMeetingKindAndSource = () => {
    dispatch(setSessionFilterMeetingKind(null))
    dispatch(setSessionFilterMeetingSources([]))
    searchParams.delete('meetingKind')
    searchParams.delete('meetingSources')
    if (onFilterChanged) {
      onFilterChanged(searchParams)
    }
  }

  const resetFilter = () => {
    reset()
    dispatch(resetSessionFilter([currentUserId]))
    const searchParams = new URLSearchParams()
    searchParams.set('userId', currentUserId)
    setSearchParams(searchParams, { replace: true })
  }

  const handleSaveFilter = async (formValues: CreateOrUpdateFilterFormValues) => {
    const searchParams = new URLSearchParams()
    try {
      if (formValues.selectedFormFilter) {
        if (formValues.selectedFormFilter.type === 'system') {
          const response = await triggerUpdateSessionFilter({
            accountId,
            filterId: formValues.selectedFormFilter.id,
            isDefault: formValues.isDefault,
          }).unwrap()
          dispatch(setSessionQuickFilter(response.data))
        } else {
          const response = await triggerUpdateSessionFilter({
            accountId,
            name: formValues.name,
            isDefault: formValues.isDefault,
            filterId: formValues.selectedFormFilter.id,
            filter: compositeFilter,
          }).unwrap()
          dispatch(setSessionQuickFilter(response.data))
        }
        searchParams.set('filterId', formValues.selectedFormFilter.id)
        setSearchParams(searchParams, { replace: true })
      } else {
        const response = await triggerCreateSessionFilter({
          accountId,
          name: formValues.name,
          isDefault: formValues.isDefault,
          filter: compositeFilter,
          filterType,
        }).unwrap()
        dispatch(setSessionQuickFilter(response.data))
        searchParams.set('filterId', response.data.id)
        setSearchParams(searchParams, { replace: true })
      }
      setModalMode(AllFilterModalMode.VIEW)
      dispatch(addToast({ variant: 'success', title: 'Success', messages: ['Success! Filter saved successfully.'] }))
    } catch (e) {
      console.error(e)
    }
  }

  const handleDeleteSelectedFilter = async () => {
    if (selectedFilter) {
      try {
        await triggerDeleteSessionFilter({ accountId, filterId: selectedFilter.id }).unwrap()
        dispatch(resetSessionFilter([currentUserId]))
        dispatch(addToast({ variant: 'success', title: 'Success', messages: ['Success! The changes have been successfully saved'] }))
        onHide()
      } catch (e) {
        console.error(e)
      }
    }
  }

  const handleShowResults = () => {
    dispatch(addToast({ variant: 'success', title: 'Success', messages: ['Success! The changes have been successfully saved'] }))
    onHide()
  }

  return (
    <Container>
      <Row className='gy-3'>
        <Col xs={12} className={classNames('all-filter-model-filter-wrapper', { 'zocks-bg-color-gray-100': selectedUserIds.length })}>
          <SessionUserFilter filterType={filterType} disabled={roomSessionsLoading || disabledForEdit} onFilterChanged={onFilterChanged} />
        </Col>
        <Col xs={12} className={classNames('all-filter-model-filter-wrapper', { 'zocks-bg-color-gray-100': selectedUserIds.length })}>
          <SessionContactFilter filterType='live' disabled={roomSessionsLoading} onFilterChanged={onFilterChanged} />
        </Col>
        <Col xs={12} className={classNames('all-filter-model-filter-wrapper', { 'zocks-bg-color-gray-100': selectedDateRange })}>
          <div className='d-flex flex-column gap-2'>
            <SessionDateFilter filterType={filterType} disabled={roomSessionsLoading || disabledForEdit} onFilterChanged={onFilterChanged} />
            {selectedDateRange ? (
              <div className='d-flex justify-content-end'>
                <div className='d-flex gap-1' role='button' onClick={resetDateRangeFilter}>
                  <i className='bi bi-arrow-clockwise' />
                  <div className='button-link-medium'>Clear</div>
                </div>
              </div>
            ) : null}
          </div>
        </Col>
        <Col xs={12} className={classNames('all-filter-model-filter-wrapper', { 'zocks-bg-color-gray-100': selectedMeetingTypeIds.length })}>
          <div className='d-flex flex-column gap-2'>
            <SessionMeetingTemplateFilter filterType={filterType} disabled={roomSessionsLoading || disabledForEdit} onFilterChanged={onFilterChanged} />
            {selectedMeetingTypeIds.length ? (
              <div className='d-flex justify-content-end'>
                <div className='d-flex gap-1' role='button' onClick={resetMeetingTypeIds}>
                  <i className='bi bi-arrow-clockwise' />
                  <div className='button-link-medium'>Clear</div>
                </div>
              </div>
            ) : null}
          </div>
        </Col>
        <Col
          xs={12}
          className={classNames('all-filter-model-filter-wrapper', { 'zocks-bg-color-gray-100': selectedMeetingKind || selectedMeetingSources.length })}
        >
          <div className='d-flex flex-column gap-2'>
            <SessionKindSourceFilter filterType={filterType} disabled={roomSessionsLoading || disabledForEdit} onFilterChanged={onFilterChanged} />
            {selectedMeetingKind || selectedMeetingSources.length ? (
              <div className='d-flex justify-content-end'>
                <div className='d-flex gap-1' role='button' onClick={resetMeetingKindAndSource}>
                  <i className='bi bi-arrow-clockwise' />
                  <div className='button-link-medium'>Clear</div>
                </div>
              </div>
            ) : null}
          </div>
        </Col>
        {filterType === 'past' ? (
          <Col xs={12} className={classNames('all-filter-model-filter-wrapper', { 'zocks-bg-color-gray-100': selectedFilterTags.length })}>
            <div className='d-flex flex-column gap-2'>
              <>
                <SessionTagsFilter filterType={filterType} disabled={roomSessionsLoading || disabledForEdit} onFilterChanged={onFilterChanged} />
                {selectedFilterTags.length ? (
                  <div className='d-flex justify-content-end'>
                    <div className='d-flex gap-1' role='button' onClick={resetTags}>
                      <i className='bi bi-arrow-clockwise' />
                      <div className='button-link-medium'>Clear</div>
                    </div>
                  </div>
                ) : null}
              </>
            </div>
          </Col>
        ) : null}
      </Row>
      <Divider className='zocks-mt-1.25 zocks-mb-1.25' />
      {modalMode === AllFilterModalMode.EDIT || modalMode === AllFilterModalMode.NEW ? (
        <Row className='gy-3'>
          <Col xs={12}>
            <Controller
              name='name'
              control={control}
              rules={{
                required: 'You must chose a name for your filter.',
                minLength: { value: 3, message: 'Filter name must be 3 characters or more' },
              }}
              render={({ field, fieldState }) => (
                <>
                  <Form.Label>Choose name*</Form.Label>
                  <Form.Control id={field.name} type='text' placeholder='Enter quick filter name' {...field} disabled={disabledForEdit} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </>
              )}
            />
          </Col>
          <Col xs={12}>
            <Controller
              name='isDefault'
              control={control}
              render={({ field }) => (
                <Form.Check id='filter-is-default-check' type='checkbox'>
                  <Form.Check.Input type='checkbox' checked={field.value} onChange={field.onChange} />
                  <Form.Check.Label>
                    <span className='paragraph-small'>Make Default</span>
                  </Form.Check.Label>
                </Form.Check>
              )}
            />
          </Col>
        </Row>
      ) : null}
      <Row className='gy-3 zocks-mt-2.0'>
        <Col sm={12} md className='d-flex align-items-center'>
          {modalMode === AllFilterModalMode.VIEW ? (
            <>
              {selectedFormFilter ? (
                <div className='d-flex gap-2 align-items-center'>
                  <span className='button-large'>{selectedFormFilter.name}</span>
                  <i role='button' className='bi bi-pencil' onClick={() => setModalMode(AllFilterModalMode.EDIT)} />
                </div>
              ) : (
                <div role='button' className='d-flex align-items-center' onClick={() => setModalMode(AllFilterModalMode.EDIT)}>
                  + Save as quick filter
                </div>
              )}
            </>
          ) : null}
          {modalMode === AllFilterModalMode.EDIT && selectedFormFilter && selectedFormFilter.type !== 'system' ? (
            <div>
              <Button variant='danger' className='button-medium' onClick={handleDeleteSelectedFilter} loading={deleteSessionFilterLoading}>
                <i className='bi bi-trash3' />
                Delete
              </Button>
            </div>
          ) : null}
        </Col>
        <Col sm={12} md='auto'>
          <div className='d-flex gap-2'>
            {modalMode === AllFilterModalMode.VIEW ? (
              <>
                <Button variant='secondary' className='flex-grow-1 button-medium' onClick={resetFilter} disabled={roomSessionsLoading}>
                  Clear all
                </Button>
                <Button variant='primary' className='flex-grow-1 button-medium' onClick={handleShowResults} loading={roomSessionsLoading}>
                  Show results {roomSessions ? <>({roomSessions.page.totalElements})</> : null}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant='secondary'
                  className='flex-grow-1 button-medium'
                  onClick={() => setModalMode(AllFilterModalMode.VIEW)}
                  disabled={roomSessionsLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant='primary'
                  className='flex-grow-1 button-medium'
                  onClick={handleSubmit(handleSaveFilter)}
                  loading={createSessionFilterLoading || updateSessionFilterLoading}
                  disabled={roomSessionsLoading}
                >
                  Save
                </Button>
              </>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  )
}
